import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { MODELOPRODUCTOS, IModeloProductosPagingRes } from '@/shared/interfaces/erp/inventario';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchModeloProductos = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetModeloProductosParams) => {
  return useQuery({
    queryKey: ['modelo-producto', page, nombre, page_size],
    queryFn: async () => {
      return getModeloProductos({ page, nombre, page_size });
    },
  });
};

export const useGetModeloProducto = (id: string) => {
  return useQuery({
    queryKey: ['modelo-producto', id],
    queryFn: async () => {
      return getModelosProduct(id);
    },
  });
};

export const useCreateModeloProduct = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createModelosProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelo-producto'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Modelo de producto creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateModeloProduct = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateModeloProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelo-producto'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Modelo de productos actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};


///* axios
export interface GetModeloProductosParams extends Partial<MODELOPRODUCTOS> {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateModeloProductParams = Omit<MODELOPRODUCTOS, 'id_modelo_producto'>;
export type UpdateModeloProductParams = {
  id: string;
  data: CreateModeloProductParams;
};

export const getModeloProductos = async (params?: GetModeloProductosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<IModeloProductosPagingRes>(
    `${urlBackend}api/v1/modelo/?${queryParams}`,
    true
  );
};

export const getModelosProduct = async (id: string) => {
  try {
    return await get<MODELOPRODUCTOS>(`${urlBackend}api/v1/modelo/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createModelosProduct = async (data: CreateModeloProductParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<MODELOPRODUCTOS>(`${urlBackend}api/v1/modelo/`, data, true);
};


export const updateModeloProduct = async ({ id, data }: UpdateModeloProductParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<MODELOPRODUCTOS>(`${urlBackend}api/v1/modelo/${id}/`, data, true);
};
